<script lang="ts" setup>
import { useFormStore } from "@stores/form.store"
import { formatAddress, formatIdSource } from "@helpers"
import {
    computed,
    inject,
    nextTick,
    onActivated,
    onDeactivated,
    ref,
    watch,
} from "vue"
import {
    ELEC_APRES,
    ELEC_BOUTON,
    ELEC_MESSAGE_MANQUANT,
    ELEC_TOGGLE_FERMER,
    ELEC_TOGGLE_OUVRIR,
    TRACKING_EVENTS,
    RECU_BOUTON,
    consentDateFin,
    elecEnedis,
    elecPhrase,
    elecTravaux,
    recuTexte,
    toFrDay,
} from "@constant"
import { isPdlConsentReceipt } from "@machine/form.machine"
import { TrackingPlugin } from "@plugins/tracking.plugin"
import { useResponsive } from "@composables"

const { $track } = inject<TrackingPlugin>("tracking")!

// Boutons empilés sous 640 px quand le libellé ne tient pas (comme FormGaz).
const { isSmallViewport } = useResponsive()

const store = useFormStore()
const { snapshot, send } = store

// =============================================================
// Autorisations (DEV-2274, spécification 2.2 et 2.3)
// =============================================================

const MASTER_INPUT_ID = "consent_pdl_all"
const ENEDIS_INPUT_ID = "consent_pdl_enedis"
const TRAVAUX_INPUT_ID = "consent_pdl_travaux"
const CONSENT_MESSAGE_ID = "consent_pdl_missing"
const CONSENT_DETAIL_ID = "consent_pdl_detail"

/** Une case de l'étape, lue et écrite dans le contexte de la machine. */
function contextFlag(field: "consent_pdl" | "consent_travaux") {
    return computed<boolean>({
        get: () => snapshot.value.context[field] === true,
        set: (value) => send({ type: "UPDATE_FIELD", field, value: !!value }),
    })
}

const consentEnedis = contextFlag("consent_pdl")
const consentTravaux = contextFlag("consent_travaux")

/** Case maître : cochée = les deux ; décochée = aucune. */
const consentAll = computed<boolean>({
    get: () => consentEnedis.value && consentTravaux.value,
    set: (value) => {
        consentEnedis.value = !!value
        consentTravaux.value = !!value
    },
})

/** État partiel : une seule des deux autorisations donnée. */
const consentPartial = computed(
    () => consentEnedis.value !== consentTravaux.value
)

// L'état indéterminé est posé AUSSI sur l'input natif (le composant ne pose
// que `aria-checked="mixed"`).
watch(
    consentPartial,
    (partial) => {
        nextTick(() => {
            const input = document.getElementById(
                MASTER_INPUT_ID
            ) as HTMLInputElement | null
            if (input) input.indeterminate = partial
        })
    },
    { immediate: true }
)

/** Détail déplié : décide aussi du texte enregistré sur DPE et FOST. */
const detailOpen = computed<boolean>({
    get: () => snapshot.value.context.consent_detail_pdl === true,
    set: (value) =>
        send({
            type: "UPDATE_FIELD",
            field: "consent_detail_pdl",
            value: !!value,
        }),
})

const dateFin = computed(() => consentDateFin(store.cstbRecord))

const phrase = computed(() => elecPhrase({ fin: dateFin.value }))
const enedis = computed(() => elecEnedis({ fin: dateFin.value }))
const travaux = computed(() => elecTravaux({ fin: dateFin.value }))

/**
 * Reçu (décision 7) : consentement PDL actif sur ce compteur ET accords DPE
 * et FOST existants. Même règle que la garde de la machine.
 */
const isReceipt = computed(() =>
    isPdlConsentReceipt(
        store.pdl,
        snapshot.value.context.pdl_idsource,
        store.cstbRecord
    )
)

const receiptText = computed(() =>
    recuTexte(toFrDay(store.pdl?.consent_signed_at))
)

const hasBothConsents = computed(
    () => consentEnedis.value && consentTravaux.value
)

/** Message au clic sans les deux autorisations. */
const showMissing = computed(
    () =>
        snapshot.value.matches({
            Step1: { FormSignConsent: "consent_missing" },
        }) &&
        !hasBothConsents.value &&
        !isReceipt.value
)

const confirmLabel = computed(() =>
    isReceipt.value ? RECU_BOUTON : ELEC_BOUTON
)

function onSubmit() {
    const consentMissing = !hasBothConsents.value && !isReceipt.value

    if (consentMissing) {
        // Événement existant, motif ajouté (pas d'événement nouveau).
        $track(TRACKING_EVENTS.FORM_INAVLID, {
            formId: snapshot.value.context.formId,
            step: snapshot.value.context.step + 1,
            motif: "consent_missing",
        })
    }

    // La machine décide : sans les deux autorisations, repli vers
    // `consent_missing`, sans aucun appel de signature.
    send({ type: "SUBMIT" })

    if (consentMissing) {
        nextTick(() => {
            // Focus sur la case qui manque : la maître repliée, la première
            // case non cochée dépliée.
            const target = !detailOpen.value
                ? MASTER_INPUT_ID
                : !consentEnedis.value
                  ? ENEDIS_INPUT_ID
                  : TRAVAUX_INPUT_ID
            document.getElementById(target)?.focus()
        })
    }
}

const timeCompletion = ref(new Date().getTime())

onActivated(() => {
    timeCompletion.value = new Date().getTime()
    $track(TRACKING_EVENTS.STEP_ENTERED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
    })
})

onDeactivated(() => {
    $track(TRACKING_EVENTS.STEP_COMPLETED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
        timeCompletion: (
            (new Date().getTime() - timeCompletion.value) /
            1000
        ).toFixed(1),
    })
})

/**
 * Un dossier existe déjà pour cet email (DEV-1660).
 *
 * Cet état n'offre PAS de réessai : la création est refusée parce que le
 * dossier existe, l'API ne rend pas son identifiant et le formulaire n'a
 * aucune route de lecture par identité (DEV-1701). Rejouer « Suivant »
 * rejouerait donc exactement le même refus. On retire le bouton plutôt que
 * de laisser le ménage tourner en rond : la reprise passe par le lien du
 * mail d'invitation, et « Retour » reste là pour corriger un email mal saisi.
 */
const isAlreadyExist = computed(() =>
    snapshot.value.matches({ Step1: { FormSignConsent: "already_exist" } })
)

const getErrorMessage = computed(() => {
    const error = snapshot.value.context.error_sign_pdl

    if (error?.code === "already-exist") {
        return "Un dossier existe déjà pour cette adresse email. Reprenez-le depuis le lien du mail d'invitation que vous avez reçu — il rouvre le formulaire là où vous vous étiez arrêté. Si l'adresse saisie est une erreur, revenez en arrière pour la corriger."
    }

    if (error) {
        return "Une erreur est survenue lors du processus d'autorisation de vos données. Veuillez réessayer plus tard."
    }
    return null
})

const visible = ref(false)
</script>

<template>
    <FormCard
        class="form-sign-consent"
        title="Adresse et compteur d'électricité"
        :confirm-label="confirmLabel"
        :horizontal-btn="isSmallViewport"
        @submit="
            () => {
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'submit-form-sign-consent',
                    buttonName: confirmLabel,
                })
                onSubmit()
            }
        "
        @back="
            () => {
                send({ type: 'BACK' })
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'back-form-sign-consent',
                    buttonName: 'Retour',
                })
            }
        "
        :loading-submit="
            snapshot.matches({ Step1: { FormSignConsent: 'sign' } })
        "
        :footer-btn="!isAlreadyExist"
    >
        <div class="form-sign-consent__description">
            <div class="form-sign-consent__line">
                <p><strong>Compteur électrique</strong></p>
                <p>
                    {{ formatIdSource(snapshot.context.pdl_idsource, "pdl") }}
                </p>
            </div>
            <div class="form-sign-consent__line">
                <p><strong>Adresse</strong></p>
                <p>{{ formatAddress(snapshot.context.address) }}</p>
            </div>
            <div class="form-sign-consent__line">
                <p><strong>Titulaire</strong></p>
                <p>
                    {{ snapshot.context.firstname }}
                    {{ snapshot.context.lastname }}
                </p>
            </div>
        </div>

        <!-- Autorisations (DEV-2274), au-dessus des boutons. Masquées pour
             un dossier déjà existant, comme l'était la mention. -->
        <div class="form-sign-consent__consent" v-if="!isAlreadyExist">
            <!-- Reçu : PDL, DPE et FOST déjà signés, compteur inchangé -->
            <p v-if="isReceipt" class="form-sign-consent__receipt">
                {{ receiptText }}
            </p>

            <div v-else class="form-sign-consent__consent-block">
                <div class="form-sign-consent__consent-head">
                    <BaseCheckbox
                        v-model="consentAll"
                        binary
                        :indeterminate="consentPartial"
                        :input-id="MASTER_INPUT_ID"
                        :invalid="showMissing"
                        :pt="{
                            input: {
                                'aria-describedby': showMissing
                                    ? CONSENT_MESSAGE_ID
                                    : undefined,
                            },
                        }"
                    />
                    <label :for="MASTER_INPUT_ID">
                        <ConsentSegments :segments="phrase" />
                    </label>
                </div>

                <BaseButton
                    variant="link"
                    size="small"
                    class="form-sign-consent__consent-toggle"
                    :label="
                        detailOpen ? ELEC_TOGGLE_FERMER : ELEC_TOGGLE_OUVRIR
                    "
                    :aria-expanded="detailOpen"
                    :aria-controls="CONSENT_DETAIL_ID"
                    @click="detailOpen = !detailOpen"
                />

                <div
                    v-show="detailOpen"
                    :id="CONSENT_DETAIL_ID"
                    class="form-sign-consent__consent-detail"
                    role="group"
                >
                    <div class="form-sign-consent__consent-head">
                        <BaseCheckbox
                            v-model="consentEnedis"
                            binary
                            :input-id="ENEDIS_INPUT_ID"
                            :invalid="showMissing && !consentEnedis"
                            :pt="{
                                input: {
                                    'aria-describedby':
                                        showMissing && !consentEnedis
                                            ? CONSENT_MESSAGE_ID
                                            : undefined,
                                },
                            }"
                        />
                        <label :for="ENEDIS_INPUT_ID">
                            <ConsentSegments :segments="enedis.lead" />
                        </label>
                    </div>
                    <ul>
                        <li v-for="item in enedis.items" :key="item">
                            {{ item }}
                        </li>
                    </ul>
                    <div class="form-sign-consent__consent-head">
                        <BaseCheckbox
                            v-model="consentTravaux"
                            binary
                            :input-id="TRAVAUX_INPUT_ID"
                            :invalid="showMissing && !consentTravaux"
                            :pt="{
                                input: {
                                    'aria-describedby':
                                        showMissing && !consentTravaux
                                            ? CONSENT_MESSAGE_ID
                                            : undefined,
                                },
                            }"
                        />
                        <label :for="TRAVAUX_INPUT_ID">
                            <ConsentSegments :segments="travaux" />
                        </label>
                    </div>
                    <p><ConsentSegments :segments="ELEC_APRES" /></p>
                </div>
            </div>

            <BaseButton
                variant="link"
                size="small"
                class="form-sign-consent__more-info"
                label="En savoir plus sur mes données"
                @click="visible = !visible"
            />

            <p
                v-if="showMissing"
                :id="CONSENT_MESSAGE_ID"
                class="form-sign-consent__consent-missing"
                role="alert"
            >
                {{ ELEC_MESSAGE_MANQUANT }}
            </p>
        </div>

        <template #footer>
            <BaseMessage severity="error" class="mb-8" v-if="getErrorMessage">
                {{ getErrorMessage }}</BaseMessage
            >

            <!--
                Dossier déjà existant : « Suivant » est retiré (`footer-btn`
                ci-dessus), on ne garde que « Retour » pour corriger un email
                mal saisi. Le bloc d'autorisation disparaît avec lui : il
                décrit ce que le bouton déclenche.
            -->
            <div
                class="form-sign-consent__blocked-footer"
                v-if="isAlreadyExist"
            >
                <BaseButton
                    class="md:w-fit w-full !min-w-fit"
                    label="Retour"
                    severity="secondary"
                    @click="
                        () => {
                            send({ type: 'BACK' })
                            $track(TRACKING_EVENTS.CLICK_BUTTON, {
                                buttonId:
                                    'back-form-sign-consent-already-exist',
                                buttonName: 'Retour',
                            })
                        }
                    "
                ></BaseButton>
            </div>

            <DialogConsent
                v-model:visible="visible"
                type="pdl"
                :date-fin="dateFin"
            />
        </template>
    </FormCard>
</template>

<style scoped>
@import "@styles/breakpoints.css";
.form-sign-consent {
    --gap-form: 3.5rem;
    --padding-form: 3.5rem;
    &:deep(.p-card-content) {
        display: flex;
        flex-direction: column;
        gap: var(--gap-form);
        padding-top: var(--padding-form);

        @media (--small-viewport) {
            --gap-form: 2.5rem;
            --padding-form: 2.5rem;
        }
    }

    .form-sign-consent__description {
        --gap-description: 2rem;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--gap-description);
        align-self: stretch;

        p {
            color: #092754;
            font-weight: 400;
        }

        @media (--small-viewport) {
            --gap-description: 1rem;
        }
    }

    .form-sign-consent__line {
        --gap-line: 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        gap: var(--gap-line);

        @media (--small-viewport) {
            flex-direction: column;
            align-items: flex-start;

            --gap-line: 0.5rem;
        }
    }

    /* Dossier déjà existant : seul « Retour » subsiste (DEV-1660). */
    .form-sign-consent__blocked-footer {
        display: flex;
        padding: 1rem 0 3.5rem;
        align-items: center;

        @media (--small-viewport) {
            display: grid;
            grid-template-columns: 1fr;
        }
    }

    /* Autorisations (DEV-2274) : maquette validée du dossier v1.1 */
    .form-sign-consent__consent {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        color: #051833;
        font-size: 0.875rem;
        line-height: 1.55;
    }

    .form-sign-consent__consent-block {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding: 1rem;
        border: 1.5px solid #092754;
        border-radius: 0.5rem;
        background: #f6f9fc;
    }

    .form-sign-consent__consent-head {
        display: grid;
        grid-template-columns: 1.625rem 1fr;
        gap: 0.625rem;
        align-items: start;

        label {
            cursor: pointer;
            font-weight: 400;
            color: #051833;
        }

        strong {
            font-weight: 700;
        }
    }

    .form-sign-consent__consent-toggle {
        align-self: flex-start;
        color: #0a5bc4;
        margin-left: 2.25rem;
        padding: 0;
        font-size: 0.8125rem;
        font-weight: 700;
        text-decoration: underline;
        @media (--small-viewport) {
            margin-left: 0;
        }
    }

    .form-sign-consent__consent-detail {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
        margin-left: 2.25rem;
        padding-top: 0.75rem;
        border-top: 1px solid #dbe3ee;

        ul {
            list-style: disc;
            padding-left: 3.5rem;
        }

        @media (--small-viewport) {
            margin-left: 0;

            ul {
                padding-left: 1.25rem;
            }
        }
    }

    .form-sign-consent__receipt {
        font-weight: 600;
        color: #092754;
    }

    .form-sign-consent__consent-missing {
        color: var(--p-red-600, #b42318);
        font-size: 0.8125rem;
        font-weight: 600;
        text-align: right;

        @media (--small-viewport) {
            text-align: left;
        }
    }

    .form-sign-consent__more-info {
        align-self: flex-start;
        color: var(--p-primary-900);
        font-size: 0.75rem;
        font-weight: 400;
        text-decoration: underline;
        padding: 0;
    }
}
</style>
